import { Handle } from '@xyflow/react';
import { getHandlePositions, getNodeFlowId } from './nodeLayout';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FLOW_TYPE_CUSTOM } from 'js/constants/pipelineConstants';
import { RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';
import { isReadOnly } from 'js/lib/ACLUtils';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import useResourceEditProvider from 'js/components/ResourceEditProvider/useResourceEditProvider';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { getFlowNodes, getNexsetLookup, resolveNexsetId } from 'js/reducers/pipeline/selectors';
import classes from '../FlowNodes.module.scss';
import { HANDLE_PIN_TO_CARD_CENTER } from '../consts';
import { useByoCodeConfigured } from './useByoCodeConfigured';

const ByoDestShellNode = memo((props: any) => {
  const { data } = props;
  const { item, layoutDirection = 'LR', isCreateFlow = false, isPreview = false } = data;
  const { setAction } = useResourceEditProvider();
  const { refreshPipelineById } = usePipelinesAdapter();
  const history = useHistory();

  const flowId = getNodeFlowId(data);
  const isByoShell = !!item?.isByoSourceShell;

  // Step 3 stays blocked until Step 2 (code) is actually done — same gate
  // CodeNode uses, so "Blocked" is never just a label on a live button.
  const { codeConfigured } = useByoCodeConfigured({ item, isByoShell, isPreview, isCreateFlow });
  const blocked = isByoShell || !codeConfigured;
  // Preview and view-only (collaborator) users never get a live button —
  // same ACL helper the other canvas cards gate on.
  const clickable = !blocked && !isPreview && !isReadOnly(item ?? {});

  const nexsetId = useSelector((state: any) => {
    const viaLookup = resolveNexsetId(getNexsetLookup(state), flowId, item?.flow_node_id);
    if (viaLookup != null) return viaLookup;

    const flowNodes = getFlowNodes(state);
    for (const childKey of item?.children ?? []) {
      const child = flowNodes[childKey] as any;
      if (child?.type === 'DATA_SET') return child.data_set_id ?? child.id ?? null;
    }
    return null;
  });

  const targetPosition = getHandlePositions(layoutDirection).target;

  const onClick = () => {
    if (isByoShell) return;
    setAction?.({
      type: 'create',
      resource: RESOURCE_TYPE_SINK,
      ...(nexsetId != null ? { nexsetId } : {}),
      queryParams: { flowId, sourceId: item?.id } as any,
      flowType: FLOW_TYPE_CUSTOM as any,
      onComplete: (result: any) => {
        // Success means a sink was actually created. `result != null` also
        // accepted `{}` — what FileSystem's in-modal back and (pre-NEX-22636)
        // the missing-nexset path dismissed with — and navigated away as if a
        // destination existed.
        if (result?.addedDataSinkId != null) {
          // ByoDestinationConfigureStep.tsx's own save() already activates the
          // newly-created sink directly (PUT /data_sinks/{id}/activate) — this
          // used to ALSO call activatePipeline (PUT /flows/{id}/activate),
          // which cascades to the same sink, producing a second, redundant
          // destination-activate call on every "Add Destination". BYO has no
          // persistent pipeline lifecycle to activate in the first place (it's
          // a one-shot Ray job, not an activate/pause pipeline) — resource-
          // level activation (source in Step 1, sink here) is the complete,
          // correct signal. Confirmed bug, fixed 2026-08-20.
          refreshPipelineById(flowId);
          // Land on the specific flow's edit page, not the generic flows
          // list — matches the established pattern from
          // ByoSourceConfigureStep.tsx's own post-save navigation. Confirmed
          // bug, fixed 2026-08-20: landing on the list after finishing the
          // full source→code→destination create sequence makes the user
          // re-find and re-open the flow they just built.
          history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/${flowId}/edit`);
        }
      },
    });
  };

  return (
    <div style={{ whiteSpace: 'nowrap' }} data-testid="byo-canvas-dest-node">
      <div style={{ position: 'relative', width: 232 }}>
        <button
          type="button"
          className={classes.codeShell}
          onClick={clickable ? onClick : undefined}
          aria-disabled={!clickable}
          style={clickable ? undefined : { cursor: 'default' }}
          data-testid="byo-dest-shell-btn"
        >
          <span className={classes.shellRow}>
            <span className={classes.addDestPlus}>+</span>
            <span className={classes.shellTitle}>Add Destination</span>
          </span>
        </button>
      </div>
      {/* Preview hides the step footer entirely, like CodeNode's. */}
      {!isPreview && (
        <div className={classes.shellFooter}>
          {blocked && <span className={classes.shellBadge}>Blocked</span>}
          <span className={classes.shellCaption}>Step 3: Add a destination</span>
        </div>
      )}
      <Handle
        isConnectable={false}
        type="target"
        position={targetPosition}
        id="left"
        style={{ top: HANDLE_PIN_TO_CARD_CENTER }}
      />
    </div>
  );
});
ByoDestShellNode.displayName = 'ByoDestShellNode';
export default ByoDestShellNode;
